import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  MessageSquare,
  Phone,
  AtSign,
  Mail,
  Globe,
  Footprints,
  Sparkles,
  Search,
  Radio,
  Voicemail,
  PhoneIncoming,
  PhoneMissed,
  Users,
  Flag,
} from 'lucide-react';
import clsx from 'clsx';
import { useBrainStore } from '@/state/brainStore';
import { useSessionStore } from '@/state/sessionStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useUIStore } from '@/state/uiStore';
import { useActiveCall } from '@/hooks/useActiveCall';
import {
  buildConversationsFeed,
  STATUS_META,
  type ConversationFeedItem,
} from '@/utils/conversationsFeed';
import { relativeFromIso, simTimeShort } from '@/utils/format';
import type { Channel, StudioId } from '@/domain/types';

const CHANNEL_ICON: Record<Channel, typeof MessageSquare> = {
  sms: MessageSquare,
  'meta-dm': MessageSquare,
  'ig-dm': AtSign,
  email: Mail,
  voice: Phone,
  'web-form': Globe,
  'walk-in': Footprints,
};

const CHANNEL_LABEL: Record<Channel, string> = {
  sms: 'SMS',
  'meta-dm': 'Messenger',
  'ig-dm': 'Instagram',
  email: 'Email',
  voice: 'Voice',
  'web-form': 'Web form',
  'walk-in': 'Walk-in',
};

const STATUS_TONE: Record<string, string> = {
  live: 'bg-blood/15 text-blood border-blood/40',
  urgent: 'bg-blood/10 text-blood border-blood/40',
  ai: 'bg-gold/15 text-gold-bright border-gold-bright/40',
  warm: 'bg-mint/10 text-mint border-mint/40',
  idle: 'bg-ink-soft text-cream-soft border-gold-dim/30',
};

type Quick = 'not-in-queue' | 'all' | 'ai' | 'awaiting' | 'missed' | 'voicemail' | 'live';

// Filters match on the whole feed item so a non-status filter (Not in a queue)
// composes with the status-based ones.
const QUICK_FILTERS: Array<{
  id: Quick;
  label: string;
  match: (item: ConversationFeedItem) => boolean;
}> = [
  {
    // The messaging team's remaining work: threads no booking agent owns yet.
    id: 'not-in-queue',
    label: 'Not in a queue',
    match: (i) => !i.inQueue,
  },
  { id: 'all', label: 'All active', match: () => true },
  {
    id: 'live',
    label: 'Live calls',
    match: (i) => i.status === 'live-call' || i.status === 'ringing-in',
  },
  {
    id: 'ai',
    label: 'AI active',
    match: (i) => i.status === 'ai-active' || i.status === 'ai-handoff',
  },
  {
    id: 'awaiting',
    label: 'Awaiting reply',
    match: (i) => i.status === 'awaiting-agent' || i.status === 'ai-handoff',
  },
  {
    id: 'missed',
    label: 'Missed calls',
    match: (i) => i.status === 'missed-call',
  },
  {
    id: 'voicemail',
    label: 'Voicemails',
    match: (i) => i.status === 'voicemail',
  },
];

const ALL_CHANNELS: Channel[] = ['sms', 'meta-dm', 'ig-dm', 'email', 'voice'];

export default function Conversations() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const customers = useBrainStore((s) => s.customers);
  const aiConversations = useBrainStore((s) => s.aiConversations);
  const activityFeed = useBrainStore((s) => s.activityFeed);
  const sequenceState = useBrainStore((s) => s.sequenceState);
  const studios = useBrainStore((s) => s.studios);
  const upsertAIConversation = useBrainStore((s) => s.upsertAIConversation);
  const incomingCalls = useUIStore((s) => s.incomingCalls);
  const clockIso = useSimulationStore((s) => s.clockIso);
  const activeCall = useActiveCall();
  const assignedStudios = useSessionStore((s) => s.assignedStudios);
  const activeStudio = useSessionStore((s) => s.activeStudio);

  // Filter state derived from URL — keeps Calls + AI Conversations redirects
  // intact (they pass ?filter=missed and ?filter=ai respectively).
  const initialQuick = (searchParams.get('filter') as Quick) || 'not-in-queue';
  const [quick, setQuick] = useState<Quick>(initialQuick);
  const [channelFilter, setChannelFilter] = useState<Set<Channel>>(new Set());
  const [studioFilter, setStudioFilter] = useState<StudioId | 'all'>(activeStudio ?? 'all');
  const [query, setQuery] = useState('');

  useEffect(() => {
    // Keep URL in sync so back/forward + bookmarking work.
    const next = new URLSearchParams(searchParams);
    if (quick !== 'not-in-queue') next.set('filter', quick);
    else next.delete('filter');
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [quick, searchParams, setSearchParams]);

  const allItems = useMemo(
    () =>
      buildConversationsFeed({
        customers,
        aiConversations,
        activityFeed,
        activeCall: activeCall
          ? { customerId: activeCall.customerId, status: activeCall.status }
          : null,
        incomingCalls,
        sequenceState,
        nowIso: clockIso,
      }),
    [customers, aiConversations, activityFeed, activeCall, incomingCalls, sequenceState, clockIso],
  );

  const quickMatcher = QUICK_FILTERS.find((q) => q.id === quick)!;
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allItems.filter((item) => {
      if (!quickMatcher.match(item)) return false;
      if (channelFilter.size > 0 && !channelFilter.has(item.latestChannel)) return false;
      if (studioFilter !== 'all' && item.studioId !== studioFilter) return false;
      if (q) {
        const c = item.customer;
        const hay = `${c.firstName} ${c.lastName} ${c.phone} ${c.suburb} ${item.preview}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [allItems, quickMatcher, channelFilter, studioFilter, query]);

  // Counts per quick filter, computed against the full list (before quick).
  const quickCounts = useMemo(() => {
    const counts = Object.fromEntries(QUICK_FILTERS.map((f) => [f.id, 0])) as Record<Quick, number>;
    for (const item of allItems) {
      for (const f of QUICK_FILTERS) {
        if (f.match(item)) counts[f.id] += 1;
      }
    }
    return counts;
  }, [allItems]);

  const toggleChannel = (c: Channel) => {
    setChannelFilter((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });
  };

  const takeOverAI = (item: ConversationFeedItem) => {
    const ai = aiConversations[`aiconv-${item.customerId.split('-')[0]}`] ??
      Object.values(aiConversations).find((c) => c.customerId === item.customerId);
    if (ai) {
      upsertAIConversation({ ...ai, state: 'resolved' });
    }
    navigate(`/customer/${item.customerId}`, { state: { handoffFromAI: true } });
  };

  return (
    <section
      className="flex h-full min-h-0 overflow-hidden"
      data-testid="conversations-inbox"
    >
      <section className="flex-1 flex flex-col min-w-0 min-h-0">
        <header className="px-6 py-4 border-b border-gold-dim/15">
          <p className="label-small-caps text-gold-bright">PRD §12.6 · §12.7 · §12.8</p>
          <h1 className="heading-display text-2xl text-cream mt-1">Conversations</h1>
          <p className="font-serif text-cream-soft mt-1 text-sm">
            Every channel in one queue. AI works what it can, you pick up the rest. Click any row
            to open the customer record.
          </p>
        </header>

        {/* Quick filter chips */}
        <div className="px-6 py-3 border-b border-gold-dim/15 flex gap-2 flex-wrap items-center">
          {QUICK_FILTERS.map((f) => {
            const active = f.id === quick;
            const count = quickCounts[f.id];
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setQuick(f.id)}
                data-testid={`conversations-filter-${f.id}`}
                className={clsx(
                  'inline-flex items-center gap-2 px-3 py-1 rounded-full border text-[11px] font-inter uppercase tracking-widest transition',
                  active
                    ? 'border-gold-bright bg-gold/15 text-gold-bright'
                    : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/50',
                )}
              >
                {f.label}
                <span
                  className={clsx(
                    'px-1.5 rounded-full text-[10px]',
                    active ? 'bg-gold-bright/30 text-gold-bright' : 'bg-ink-soft text-cream-soft',
                  )}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Secondary filters */}
        <div className="px-6 py-3 border-b border-gold-dim/15 flex items-center gap-4 flex-wrap">
          <label className="flex items-center gap-2 flex-1 min-w-[240px]">
            <Search size={12} className="text-cream-faint" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, phone, suburb, or message…"
              className="flex-1 bg-transparent text-sm text-cream focus:outline-none"
              data-testid="conversations-search"
            />
          </label>
          <div className="flex items-center gap-1 flex-wrap">
            <span className="font-inter text-[10px] uppercase tracking-widest text-cream-faint mr-1">
              Channel
            </span>
            {ALL_CHANNELS.map((c) => {
              const Icon = CHANNEL_ICON[c];
              const on = channelFilter.has(c);
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => toggleChannel(c)}
                  className={clsx(
                    'inline-flex items-center gap-1 px-2 py-1 rounded-full border text-[10px] font-inter uppercase tracking-widest transition',
                    on
                      ? 'border-gold-bright/60 bg-gold/10 text-gold-bright'
                      : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/40',
                  )}
                  data-testid={`conversations-channel-${c}`}
                >
                  <Icon size={10} /> {CHANNEL_LABEL[c]}
                </button>
              );
            })}
          </div>
          {assignedStudios.length > 1 ? (
            <select
              value={studioFilter}
              onChange={(e) => setStudioFilter(e.target.value as StudioId | 'all')}
              className="bg-ink-deep border border-gold-dim/40 rounded px-2 py-1 text-xs text-cream"
              data-testid="conversations-studio-filter"
            >
              <option value="all">All studios</option>
              {assignedStudios.map((id) => (
                <option key={id} value={id}>
                  {studios[id]?.name ?? id}
                </option>
              ))}
            </select>
          ) : null}
        </div>

        {/* Inbox list */}
        <ol
          className="flex-1 min-h-0 overflow-y-auto"
          data-testid="conversations-list"
        >
          {filtered.length === 0 ? (
            <li className="px-6 py-12 text-center font-serif text-cream-soft">
              No conversations match these filters.
            </li>
          ) : null}
          {filtered.map((item) => (
            <ConversationRow
              key={item.customerId}
              item={item}
              onOpen={() => navigate(`/customer/${item.customerId}`)}
              onTakeOver={() => takeOverAI(item)}
            />
          ))}
        </ol>
      </section>
    </section>
  );
}

function ConversationRow({
  item,
  onOpen,
  onTakeOver,
}: {
  item: ConversationFeedItem;
  onOpen: () => void;
  onTakeOver: () => void;
}) {
  const ChannelIcon = CHANNEL_ICON[item.latestChannel] ?? MessageSquare;
  const meta = STATUS_META[item.status];
  const handler = item.handler;
  const StatusIcon =
    item.status === 'live-call'
      ? Radio
      : item.status === 'ringing-in'
        ? PhoneIncoming
        : item.status === 'voicemail'
          ? Voicemail
          : item.status === 'missed-call'
            ? PhoneMissed
            : item.status === 'ai-active' || item.status === 'ai-handoff'
              ? Sparkles
              : item.status === 'awaiting-agent'
                ? MessageSquare
                : Users;
  const isAiRow = handler.kind === 'ai-intake' || handler.kind === 'ai-voice';

  return (
    <li
      data-testid={`conversation-row-${item.customerId}`}
      className={clsx(
        'border-b border-gold-dim/10 hover:bg-ink-soft/40 transition',
        item.awaitingResponse ? 'bg-gold/[0.02]' : '',
        // Owned by a booking agent — greyed so the messaging team does not double-work it.
        item.inQueue ? 'opacity-50' : '',
      )}
    >
      <button
        type="button"
        onClick={onOpen}
        className="w-full text-left px-6 py-3 flex items-center gap-4"
      >
        {/* Status chip — an "In queue" flag replaces it once a booking agent owns the thread. */}
        {item.inQueue ? (
          <span
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-[10px] font-inter uppercase tracking-widest shrink-0 min-w-[120px] bg-azure/15 text-azure border-azure/40"
            data-testid={`conversation-status-${item.customerId}`}
          >
            <Flag size={10} />
            In queue
          </span>
        ) : (
          <span
            className={clsx(
              'inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-[10px] font-inter uppercase tracking-widest shrink-0 min-w-[120px]',
              STATUS_TONE[meta.tone],
              item.status === 'live-call' || item.status === 'ringing-in' ? 'animate-pulse' : '',
            )}
            data-testid={`conversation-status-${item.customerId}`}
          >
            <StatusIcon size={10} />
            {meta.label}
          </span>
        )}

        {/* Customer + channel + preview */}
        <div className="flex-1 min-w-0">
          <header className="flex items-baseline gap-2 flex-wrap">
            <span className="font-inter text-sm text-cream truncate">
              {item.customer.firstName} {item.customer.lastName}
            </span>
            <span className="font-inter text-[10px] uppercase tracking-widest text-cream-faint">
              <ChannelIcon size={9} className="inline mr-1" />
              {CHANNEL_LABEL[item.latestChannel]}
            </span>
            <span className="font-inter text-[10px] uppercase tracking-widest text-cream-faint">
              {item.customer.primaryStudioId.replace('-', ' ')}
            </span>
            {item.customer.designIntent ? (
              <span className="font-inter text-[10px] uppercase tracking-widest text-cream-faint">
                {item.customer.designIntent.type.replace('-', ' ')}
              </span>
            ) : null}
          </header>
          <p className="font-serif text-sm text-cream-soft mt-0.5 truncate">{item.preview}</p>
          {item.inQueue ? (
            <p
              className="mt-1 flex items-center gap-1.5 font-inter text-[10px] uppercase tracking-widest text-azure"
              data-testid={`conversation-in-queue-flag-${item.customerId}`}
            >
              <Flag size={9} /> In queue · an agent will respond
            </p>
          ) : null}
        </div>

        {/* Handler + time */}
        <div className="shrink-0 text-right">
          <p
            className={clsx(
              'font-inter text-[10px] uppercase tracking-widest',
              isAiRow ? 'text-gold-bright' : 'text-cream-soft',
            )}
          >
            {handler.label}
          </p>
          <p className="font-mono text-[10px] text-cream-faint mt-0.5">
            {simTimeShort(item.lastActivityIso)} · {relativeFromIso(item.lastActivityIso)}
          </p>
        </div>

        {/* Quick actions */}
        {isAiRow && item.status === 'ai-handoff' ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onTakeOver();
            }}
            data-testid={`conversation-take-over-${item.customerId}`}
            className="btn-primary text-[10px] shrink-0"
          >
            <Sparkles size={10} /> Take over
          </button>
        ) : null}
      </button>
    </li>
  );
}
